import { UseQueryOptions } from 'react-query';

export interface QueryParams<T> {
  pageNumber?: number;
  pageSize?: number;
  filters?: string;
  sortOrder?: string;
  includeParentOrg?: boolean;
  includeSsoConnection?: boolean;
  options?: UseQueryOptions<T>;
}

/**
 * Region is stored as a BCP 47 culture code. Two values are allowed today:
 * `en-US` (US) and `en-IE` (Non-US). The user-facing labels remain "US" /
 * "Non-US" — only the stored culture code differs.
 */
export const OrganizationRegions = {
  US: 'en-US',
  NonUS: 'en-IE',
} as const;

export type OrganizationRegion = (typeof OrganizationRegions)[keyof typeof OrganizationRegions];

/**
 * The OncoLens root organization sits at the top of the org tree. A "top-level"
 * (parent) organization is one parented directly to it — those are the only orgs
 * where the Region setting is configured; child orgs inherit it.
 * Mirrors `WellKnownIds.OncoLensOrgId` in the Administration backend.
 */
export const OncoLensRootOrganizationId = '18d34530-af7d-48da-a0ac-3a0e3e8355e2';

export interface OrganizationSettingsDto {
  requiresUserConsent: boolean;
  consentText: string;
  region: OrganizationRegion;
  treatmentPlanDisclaimer: string;
  treatmentPlanTemplate: string;
  medicalHistoryTemplate: string;
  virtualConferenceInfoPanelEnabled?: boolean;
  virtualConferenceAudioRecording?: boolean;
  autoFillFacilitiesForAccreditation?: boolean;
  allowPhiAccessManagement: boolean;
  sendPresentingProviderOnWriteback?: boolean;
  newCasePostedEmailAlertsEnabled?: boolean;
  dailyReminderPathology?: boolean;
  dailyReminderRadiology?: boolean;
  dailyReminderGenetics?: boolean;
  dailyReminderAdministration?: boolean;
}

export interface OrganizationDto {
  id: string;
  name: string;
  referenceCode: string;
  parentOrganizationId: string;
  parentOrganization?: OrganizationDto;
  organizationSettings: OrganizationSettingsDto;
  ssoIntegrationStatus: string;
  domains: string[];
  salesForceId: string;
  logoS3Key?: string;
  /**
   * Public URL segment for the org's Collaborate form — `/c/{slug}/{form}`. Surfaced in the
   * UI as "Collaborate form URL"; the `slug` name is retained here to match the API/DB field.
   * Distinct from `referenceCode`, which is the EMR/integration identifier.
   */
  slug?: string;
  /** Accent colour applied to the org's public Collaborate form, as a `#rrggbb` hex string. */
  brandColor?: string;
}

export interface OrganizationSettingsForUpdateDto {
  requiresUserConsent: boolean;
  consentText: string;
  region: OrganizationRegion;
  disallowPhi: boolean;
  allowPhiAccessManagement: boolean;
  showLabRecommendationsInRecommendationBuilder: boolean;
  treatmentPlanDisclaimer: string;
  treatmentPlanTemplate: string;
  medicalHistoryTemplate: string;
  virtualConferenceInfoPanelEnabled: boolean;
  virtualConferenceAudioRecording: boolean;
  autoFillFacilitiesForAccreditation: boolean;
  sendPresentingProviderOnWriteback: boolean;
  newCasePostedEmailAlertsEnabled: boolean;
  dailyReminderPathology: boolean;
  dailyReminderRadiology: boolean;
  dailyReminderGenetics: boolean;
  dailyReminderAdministration: boolean;
}

export interface OrganizationForUpdateDto {
  name: string;
  referenceCode: string;
  organizationSettings: OrganizationSettingsForUpdateDto;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  salesForceId: string;
  domains: string[];
  slug?: string;
  brandColor?: string;
}

export interface SsoConnectionFileDto {
  fileName?: string;
  certificate?: string;
  isNewFile: boolean;
}

export interface SsoConnectionForManipulationDto {
  file: SsoConnectionFileDto;
  connectionName: string;
  signInUrl?: string;
  signOutUrl?: string;
  enableSignOut: boolean;
  enableIdpInitiation: boolean;
  organizationId: string | undefined;
  domainAliases: string;
}

export interface SsoConnectionDto {
  id: string;
  file: SsoConnectionFileDto;
  connectionName: string;
  signInUrl: string;
  signOutUrl: string;
  enableSignOut: boolean;
  enableIdpInitiation: boolean;
  status: string;
  organizationId: string;
  domainAliases: string;
}

export const Licenses = {
  EmrIntegrations: 'EMR Integrations',
  LabIntegrations: 'Lab Integrations',
  MolecularRecommendations: 'Molecular Recommendations',
  VirtualConference: 'Virtual Conference',
} as const;

export type OrganizationLicenseDto = {
  id: string;
  organizationId: string;
  license: string;
};

export type License = (typeof Licenses)[keyof typeof Licenses];
